import axios from "axios";
import { computed, reactive, readonly, ref, shallowReadonly, toRaw } from "vue";
import { defineStore } from "pinia";
import { useAsyncOperation } from "Admin/js/Composables/useAsyncOperation";
import * as TTL from "./TimeTrackerConstants.js";
import { useUserStore } from "./User";
import Utilities from "../Helpers/utilities";
import DateUtilities from "../Utilities/DateUtilities";
import { useConversation } from "./Conversation";
import HttpStatusCodes from "../Constants/http";
import { useWorkspaceTree } from "Admin/js/Stores/WorkspaceTree.js";
import { useTeamPanel } from "Admin/js/Stores/TeamPanel.js";
import { useAppState } from "./AppState";

/**
 * Shift / workday / task timer store.
 *
 * Design notes:
 * - All reactive state is private. Components access it through the
 *   explicitly returned surface — every state field is wrapped in `readonly`.
 * - The only way to change state is through the exposed API actions and setters.
 * - Data placement functions (`place*`) are the single source of truth for
 *   hydrating state from server payloads — equivalent to WorkspaceTree's `place*Data`.
 * - Network I/O goes through `manageAsyncOperation` with one explicit loading
 *   ref per concern so the UI can independently track each operation.
 */
export const useTimeTrackerStore = defineStore("timeTrackerStore", () => {
    const { manageAsyncOperation } = useAsyncOperation();

    function notifyTimeTrackerApiError(err) {
        if (err?.response?.status === HttpStatusCodes.FORBIDDEN) {
            Utilities().showErrorSnackbar("Permission Denied");
        }
    }

    // ─────────────────────────────────────────────────────────────────────────
    // API URLS
    // Never hard-coded in components. Exposed in return so tests can inspect
    // which endpoint a given action posts to.
    // ─────────────────────────────────────────────────────────────────────────

    const URLS = {
        pausedItems: "/shift-timer/shift/paused-task-items",
        shiftLog: "/shift-timer/shift/log",
        shiftWorkLog: "/shift-timer/shift/work-log",
        shiftStatus: "/shift-timer/shift/status",
        taskLog: "/shift-timer/task/play",
        timerSync: "/shift-timer/shift/status/sync",
        workdayStart: "/workdays/start",
        workdayEnd: "/workdays/end",
    };

    // ─────────────────────────────────────────────────────────────────────────
    // LOADING STATE
    // One boolean ref per logical operation group. Naming convention:
    //   isLoading* → read / fetch operations
    //   isSaving*  → user-initiated write operations (shift in/out, task start…)
    //   isSyncing* → background heartbeat (3-min timer sync, no UI spinner)
    // Pass the ref's setter as the `setLoading` callback to `manageAsyncOperation`
    // so it is toggled automatically on start / finish.
    // ─────────────────────────────────────────────────────────────────────────

    const isLoadingShiftSnapshot = ref(false);
    const isLoadingPausedTaskList = ref(false);
    const isLoadingViewedTaskDuration = ref(false);
    const isLoadingWorkLogs = ref(false);
    const isLoadingDownloadLinks = ref(false);

    const isSavingShiftAction = ref(false);
    const isSavingTaskAction = ref(false);
    const isSavingWorkday = ref(false);

    const isSyncingTimer = ref(false);

    // ─────────────────────────────────────────────────────────────────────────
    // SHIFT STATE
    // The currently active shift for the signed-in user.
    // Write exclusively through API actions (shiftIn / shiftPause / …) or
    // data-placement functions — never mutate directly from outside this store.
    // ─────────────────────────────────────────────────────────────────────────

    const _shiftTimerInterval = ref(null);
    const _shiftStartTime = ref(null);
    const _shiftBaseSeconds = ref(0);

    const shift = reactive({
        status: null,
        source: null,
        duration: 0,
        time: { hours: 0, minutes: 0, seconds: 0 },
    });

    // ─────────────────────────────────────────────────────────────────────────
    // TASK STATE
    // The task currently tracked by the signed-in user's shift timer.
    // Write exclusively through API actions (taskStart / taskPause / …) or
    // data-placement functions.
    // ─────────────────────────────────────────────────────────────────────────

    const _taskTimerInterval = ref(null);
    const _taskStartTime = ref(null);
    const _taskBaseSeconds = ref(0);

    const task = reactive({
        id: null,
        name: null,
        status: TTL.Task_Stop,
        duration: 0,
        hasStarted: false,
        time: { hours: 0, minutes: 0, seconds: 0 },
    });

    const space_paused_items = ref([]);
    const is_space_paused_items = ref(false);

    const confirm_task_start = ref(null);
    const click_task_item = ref(null);
    const is_prepared = ref(false);
    const is_auto_paused = ref(false);
    const is_task_explicitly_stopped = ref(false);
    const shift_resume_by_task_start = ref(false);
    const isOneTimeBlockShiftSync = ref(false);

    const viewed_task = reactive({
        id: null,
        duration: null,
        total_time: null,
    });

    /** All-time/today baselines for the running task — independent of viewed_task. */
    const active_task_duration = reactive({
        id: null,
        duration: null,
        total_time: null,
    });

    // ─────────────────────────────────────────────────────────────────────────
    // WORKDAY STATE
    // ─────────────────────────────────────────────────────────────────────────

    const workday_status = ref(null);
    const workday_started_at = ref(null);
    const workday_office_status = ref(null);
    const can_edit_workday = ref(false);
    const shift_segment_id = ref(
        localStorage.getItem("shift_segment_id") || null,
    );

    // ─────────────────────────────────────────────────────────────────────────
    // UI STATE
    // Workstation-Connect confirmation dialog and WS app install links.
    // Components read this state; they trigger mutations through the setters
    // section below.
    // ─────────────────────────────────────────────────────────────────────────

    const ws_confirm_dialog = reactive({
        show: false,
        title: "Kaamfu Workstation Connect",
        subtitle:
            "Click on your platform below, to download Kaamfu Workstation Connect.",
        icon: "/images/svg/computer.svg",
    });

    const wscAppStatus = ref(null);
    const downloadLinks = ref([]);

    // ─────────────────────────────────────────────────────────────────────────
    // COMPUTED — SHIFT
    // ─────────────────────────────────────────────────────────────────────────

    const is_shift_in = computed(() => shift.status === TTL.Shift_In);
    const is_shift_resumed = computed(() => shift.status === TTL.Shift_Resume);
    const is_shift_paused = computed(() => shift.status === TTL.Shift_Pause);
    const is_shift_out = computed(() => shift.status === TTL.Shift_Out);

    const has_shift_started = computed(
        () => is_shift_in.value || is_shift_resumed.value,
    );
    const is_to_show_resume_and_stop = computed(
        () => has_shift_started.value || is_shift_paused.value,
    );
    const is_shift_source_web = computed(() => shift.source === "web");

    /**
     * Pre-shaped shift summary for the timer rail and status chips.
     * Prefer consuming this over reading raw `shift.*` directly.
     */
    const activeShift = computed(() => ({
        status: shift.status,
        source: shift.source,
        durationMinutes: shift.duration,
        time: shift.time,
        isRunning: has_shift_started.value,
        isPaused: is_shift_paused.value,
        isOut: is_shift_out.value,
    }));

    // ─────────────────────────────────────────────────────────────────────────
    // COMPUTED — TASK
    // ─────────────────────────────────────────────────────────────────────────

    const is_task_started = computed(() => task.status === TTL.Task_Start);
    const is_task_resumed = computed(() => task.status === TTL.Task_Resume);
    const is_task_paused = computed(() => task.status === TTL.Task_Pause);
    const is_task_stopped = computed(() => task.status === TTL.Task_Stop);

    const is_display_task_tracker = computed(
        () =>
            (is_shift_in.value ||
                is_shift_resumed.value ||
                is_shift_paused.value) &&
            task.status &&
            !is_task_stopped.value &&
            is_workday_started.value,
    );

    const isCurrentShiftPauseOrUnreachableOrInActive = computed(
        () =>
            shift.status === TTL.Shift_Pause_Unreachable ||
            shift.status === TTL.Shift_Pause_InActive,
    );

    /**
     * Pre-shaped task summary for the timer rail.
     * Prefer consuming this over reading raw `task.*` directly.
     */
    const activeTask = computed(() => ({
        id: task.id,
        name: task.name,
        status: task.status,
        hasStarted: task.hasStarted,
        durationMinutes: task.duration,
        duration_in_minutes: task.duration,
        time: task.time,
        isRunning: is_task_started.value || is_task_resumed.value,
        isPaused: is_task_paused.value,
        isStopped: is_task_stopped.value,
    }));

    // ─────────────────────────────────────────────────────────────────────────
    // COMPUTED — WORKDAY / OFFICE
    // ─────────────────────────────────────────────────────────────────────────

    const is_workday_started = computed(
        () => workday_status.value === TTL.Workday_Started && is_prepared.value,
    );

    const is_show_resume_and_stop_workday = computed(
        () => workday_status.value !== TTL.Workday_Stopped,
    );

    const is_show_workday_stopped = computed(
        () => workday_status.value === TTL.Workday_Stopped,
    );

    /** Always false — placeholder for a future workday-pause feature. */
    const is_workday_pause = computed(() => false);

    const paused_items = computed(() => space_paused_items.value);
    const is_workday_office_open = computed(
        () => workday_office_status.value === TTL.Office_Open,
    );
    const is_workday_office_close = computed(
        () => workday_office_status.value === TTL.Office_Close,
    );
    const is_workday_past_office_hours = computed(
        () => workday_office_status.value === TTL.Past_Office_Hours,
    );

    // ─────────────────────────────────────────────────────────────────────────
    // CLOCK HELPERS  (internal — do not call from outside this store)
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Converts `durationMinutes` into h/m/s and writes it onto the `target`
     * clock object in place. Used to restore a timer display from a server
     * duration value without restarting the interval.
     *
     * @param {{ hours: number, minutes: number, seconds: number }} target
     * @param {number} durationMinutes
     */
    function applyDurationToClock(target, durationMinutes) {
        const totalSeconds = Math.round(durationMinutes * 60);
        target.hours = Math.floor(totalSeconds / 3600);
        target.minutes = Math.floor((totalSeconds % 3600) / 60);
        target.seconds = totalSeconds % 60;
    }

    function shiftTimerDurationMinutes() {
        return shift.time.minutes + shift.time.hours * 60;
    }

    function taskTimerDurationMinutes() {
        return task.time.minutes + task.time.hours * 60;
    }

    function stopTaskTimer() {
        clearInterval(_taskTimerInterval.value);
        _taskTimerInterval.value = null;
    }

    function stopShiftTimer() {
        stopTaskTimer();
        clearInterval(_shiftTimerInterval.value);
        _shiftTimerInterval.value = null;
    }

    function startTaskTimer() {
        stopTaskTimer();
        if (!is_shift_in.value && !is_shift_resumed.value) {
            task.status = TTL.Task_Stop;
            return;
        }

        _taskStartTime.value = Date.now();
        _taskBaseSeconds.value = Math.round(task.duration * 60);

        _taskTimerInterval.value = setInterval(() => {
            const elapsedSeconds = Math.floor(
                (Date.now() - _taskStartTime.value) / 1000,
            );
            const totalSeconds = _taskBaseSeconds.value + elapsedSeconds;

            applyDurationToClock(task.time, totalSeconds / 60);
        }, 1000);
    }

    function startShiftTimer() {
        if (_shiftTimerInterval.value) {
            clearInterval(_shiftTimerInterval.value);
            _shiftTimerInterval.value = null;
        }

        _shiftStartTime.value = Date.now();
        _shiftBaseSeconds.value = Math.round(shift.duration * 60);

        _shiftTimerInterval.value = setInterval(() => {
            const elapsedSeconds = Math.floor(
                (Date.now() - _shiftStartTime.value) / 1000,
            );
            const totalSeconds = _shiftBaseSeconds.value + elapsedSeconds;

            const oldMinutes = shift.time.minutes;
            applyDurationToClock(shift.time, totalSeconds / 60);
            const newMinutes = shift.time.minutes;

            if (newMinutes !== oldMinutes && newMinutes % 3 === 0) {
                window.timeOutHelper?.(() => {
                    performTimerSyncWithServer({ noLoading: true });
                }, 0);
            }

            if (
                (is_task_started.value || is_task_resumed.value) &&
                !_taskTimerInterval.value
            ) {
                startTaskTimer();
            }
        }, 1000);
    }

    function restoreShiftTimer() {
        applyDurationToClock(shift.time, shift.duration);
    }

    function restoreTaskTimer() {
        applyDurationToClock(task.time, task.duration);
    }

    function resetShiftTimer() {
        stopShiftTimer();
        shift.time.hours = shift.time.minutes = shift.time.seconds = 0;
    }

    function restoreTaskStatus() {
        restoreTaskTimer();
        if (is_task_started.value) {
            startTaskTimer();
            task.status = TTL.Task_Start;
            task.hasStarted = true;
            return;
        }
        if (is_task_resumed.value) {
            startTaskTimer();
            task.status = TTL.Task_Resume;
            task.hasStarted = true;
            return;
        }
        if (is_task_paused.value) {
            stopTaskTimer();
            task.status = TTL.Task_Pause;
            task.hasStarted = false;
            return;
        }
        stopTaskTimer();
        task.status = TTL.Task_Stop;
        task.hasStarted = false;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // DATA PLACEMENT  (internal — single writers for server truth)
    // Called exclusively after API responses.  Mirrors WorkspaceTree's
    // `place*Data` pattern:  normalise the raw payload, write to reactive
    // state, and restart timers in one place — never inline.
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Hydrates the task reactive object from a shift-status payload row.
     * Called by `placeShiftSnapshotFromServer` and `placePartialShiftFromServer`.
     *
     * @param {{ task_item_id, task_item_name, task_item_status, task_duration_in_minutes }} data
     */
    function placeTaskFromServerPayload(data) {
        task.id = data?.task_item_id ?? null;
        task.name = data?.task_item_name ?? null;
        task.duration = data?.task_duration_in_minutes ?? 0;
        task.status = data?.task_item_status ?? TTL.Task_Stop;
        task.hasStarted = isTaskRunning(task.status);
        restoreTaskTimer();

        if (task.id && isTaskRunning(task.status)) {
            const treeTotal =
                useWorkspaceTree().getItemById(task.id)?.version
                    ?.duration_in_minutes ?? 0;
            if (treeTotal > 0) {
                setActiveTaskDurationBaselines(
                    task.id,
                    task.duration ?? 0,
                    treeTotal,
                );
            }
        }
    }

    function resetTaskState() {
        task.id = task.name = null;
        task.status = TTL.Task_Stop;
        task.duration = 0;
        task.hasStarted = false;
        task.time.hours = task.time.minutes = task.time.seconds = 0;
        clearActiveTaskDurationBaselines();
    }

    function setActiveTaskDurationBaselines(itemId, todayMinutes, totalMinutes) {
        active_task_duration.id = itemId;
        active_task_duration.duration = todayMinutes;
        active_task_duration.total_time = totalMinutes;
    }

    function clearActiveTaskDurationBaselines() {
        active_task_duration.id = null;
        active_task_duration.duration = null;
        active_task_duration.total_time = null;
    }

    async function fetchTaskDurationBaselinesFromServer(itemId) {
        const { data } = await axios.post(
            `/user-profile-tasks/${itemId}/time-tracked`,
            {
                timezone: DateUtilities.getTimezone(),
                date: DateUtilities.getStartAndEndDateFromToday(0),
            },
        );

        return {
            todayMinutes: data.data.today_minutes,
            totalMinutes: data.data.total_minutes,
        };
    }

    function applyTaskDurationBaselinesToStores(itemId, todayMinutes, totalMinutes) {
        const tree = useWorkspaceTree();
        const existing = tree.getItemById(itemId);
        if (existing) {
            tree.syncItemById(itemId, {
                version: {
                    ...(existing.version ?? {}),
                    duration_in_minutes: totalMinutes,
                },
            });
        }

        const conversation = useConversation().getConversationByItemId(itemId);
        if (conversation?.state.originalData?.item) {
            const item = tree.getItemById(itemId);
            if (!conversation.state.originalData.item.version && item) {
                useConversation().updateOriginalDataByItem(item);
            }
            if (!conversation.state.originalData.item.version) {
                conversation.state.originalData.item.version = {};
            }
            conversation.state.originalData.item.version.duration_in_minutes =
                totalMinutes;
        }

        useTeamPanel().team_panel_data?.forEach((profile) => {
            profile?.data?.forEach((itemGroup) => {
                const row = itemGroup?.items?.find((gi) => gi.item_id == itemId);
                if (row) {
                    row.item.version.duration_in_minutes = totalMinutes;
                }
            });
        });

        useTeamPanel().selected_member?.tasks?.data?.forEach((group) => {
            const row = group?.items?.find((gi) => gi.item_id == itemId);
            if (row) {
                row.item.version.duration_in_minutes = totalMinutes;
            }
        });

        if (task.id === itemId) {
            setActiveTaskDurationBaselines(itemId, todayMinutes, totalMinutes);
        }
    }

    function syncActiveTaskDurationBaselines(itemId = task.id) {
        if (!itemId) {
            return Promise.resolve(null);
        }

        return fetchTaskDurationBaselinesFromServer(itemId)
            .then(({ todayMinutes, totalMinutes }) => {
                applyTaskDurationBaselinesToStores(
                    itemId,
                    todayMinutes,
                    totalMinutes,
                );
                return todayMinutes;
            })
            .catch(() => null);
    }

    function seedActiveTaskDurationFromPausedList(itemId = task.id) {
        const pausedRow = space_paused_items.value.find((i) => i.id === itemId);
        if (!pausedRow) {
            return;
        }

        setActiveTaskDurationBaselines(
            itemId,
            pausedRow.duration_in_minutes ?? task.duration ?? 0,
            pausedRow.total_duration ?? 0,
        );
    }

    /**
     * Full hydration of shift + task + workday state from a `/shift-timer/shift/status` response.
     * Called after `syncShiftStatusFromServer`. Stops all timers first then
     * restarts only what the server says is running.
     *
     * @param {object} data  Full status API response
     */
    function placeShiftSnapshotFromServer(data) {
        stopShiftTimer();

        // ── workday ─────────────────────────────────────────────────────────
        workday_status.value = data?.workday_status ?? null;
        workday_office_status.value = data?.workday_office_status ?? null;
        can_edit_workday.value = data?.can_edit_workday ?? false;
        wscAppStatus.value = data?.wsc_app ?? null;
        workday_started_at.value = data?.workday_started_at ?? null;

        // ── shift ────────────────────────────────────────────────────────────
        shift.duration = data?.shift_duration_in_minutes ?? 0;
        shift.status = data?.shift_status ?? null;
        shift.source = data?.shift_source ?? null;

        // ── task ─────────────────────────────────────────────────────────────
        // Determine whether to take the server's task or keep the local paused
        // list as the source of truth.
        const list = space_paused_items.value;
        const hasLocalPauseList = list.length > 0;
        const hasServerTask = !!data?.task_item_id;
        const serverAndLocalMismatch =
            hasLocalPauseList && list.every((t) => t.id !== data?.task_item_id);

        if (!hasLocalPauseList || (hasServerTask && !serverAndLocalMismatch)) {
            placeTaskFromServerPayload(data);
        } else if (!hasServerTask) {
            task.status = TTL.Task_Stop;
            task.hasStarted = false;
            restoreLastPauseTask();
        } else {
            restoreLastPauseTask();
        }

        restoreShiftTimer();

        // ── restart timers based on resolved state ───────────────────────────
        if (data?.task_item_status === TTL.Task_Pause) {
            stopTaskTimer();
            task.status = TTL.Task_Pause;
            task.hasStarted = false;
        }

        if (data?.task_item_status === TTL.Task_Resume) {
            startTaskTimer();
            task.status = TTL.Task_Resume;
            task.hasStarted = true;
        }

        const isShiftPauseLike =
            is_shift_paused.value ||
            shift.status === TTL.Shift_Pause_Unreachable ||
            shift.status === TTL.Shift_Pause_InActive;

        if (is_shift_in.value || is_shift_resumed.value) {
            if (!shift_segment_id.value) {
                const newId = Date.now().toString();
                shift_segment_id.value = newId;
                localStorage.setItem("shift_segment_id", newId);
            }
            startShiftTimer();
        } else if (!isShiftPauseLike) {
            // Shift ended (e.g. server-side timeout) or unknown — must not keep a
            // stale segment or SAME_SHIFT_SEGMENT flag-queue resume matches wrongly.
            shift_segment_id.value = null;
            localStorage.removeItem("shift_segment_id");
        }

        if (is_shift_paused.value) {
            stopShiftTimer();
        }

        if (task.status === TTL.Task_Start) {
            restoreTaskStatus();
        }

        is_prepared.value = true;

        if (isTaskRunning(task.status) && task.id) {
            syncActiveTaskDurationBaselines(task.id);
        }
    }

    /**
     * Partial hydration after a shift-activity LiveSync event.
     * Only updates shift/task status — does not restart the full timer
     * machinery from scratch.
     *
     * @param {object} data  Full status API response (same shape as full snapshot)
     */
    function placePartialShiftFromServer(data) {
        workday_status.value = data?.workday_status ?? null;
        workday_office_status.value = data?.workday_office_status ?? null;
        can_edit_workday.value = data?.can_edit_workday ?? false;

        const serverShiftIsPaused =
            data?.shift_status === TTL.Shift_Pause ||
            data?.shift_status === TTL.Shift_Pause_Unreachable ||
            data?.shift_status === TTL.Shift_Pause_InActive;

        const localShiftWasRunning =
            is_shift_in.value || is_shift_resumed.value;
        const localTaskIsActive =
            is_task_started.value || is_task_resumed.value;

        shift.duration = data?.shift_duration_in_minutes ?? 0;
        shift.status = data?.shift_status ?? null;

        if (data?.shift_status === TTL.Shift_Out) {
            shift_segment_id.value = null;
            localStorage.removeItem("shift_segment_id");
        }

        // Server paused the shift while our task was running — pause the task locally.
        if (
            serverShiftIsPaused &&
            localShiftWasRunning &&
            localTaskIsActive &&
            !data?.task_item_status
        ) {
            task.status = TTL.Task_Pause;
            stopTaskTimer();
            useUserStore().updateCurrentUserStatus({
                task_status: TTL.Task_Pause,
            });
        }

        // Server says a previously-paused task should resume — surface the confirm dialog.
        if (
            data?.task_item_status === TTL.Task_Pause &&
            is_task_paused.value &&
            (is_shift_in.value || is_shift_resumed.value)
        ) {
            confirm_task_start.value = {
                id: data?.task_item_id ?? null,
                name: data?.task_item_name ?? null,
                duration: data?.task_duration_in_minutes ?? 0,
                status:
                    data?.task_item_status === TTL.Task_Pause
                        ? TTL.Task_Resume
                        : TTL.Task_Start,
            };
        } else if (
            (is_shift_in.value || is_shift_resumed.value) &&
            data?.task_item_status === TTL.Task_Resume &&
            !is_task_explicitly_stopped.value
        ) {
            placeTaskFromServerPayload(data);
        } else if (
            serverShiftIsPaused &&
            data?.task_item_status &&
            data?.task_item_status !== TTL.Task_Resume
        ) {
            placeTaskFromServerPayload(data);
            if (
                task.status === TTL.Task_Pause ||
                task.status === TTL.Task_Stop
            ) {
                stopTaskTimer();
            }
        }

        is_prepared.value = true;
        shift_resume_by_task_start.value = false;
    }

    /**
     * Hydrate a paused-task confirm-dialog entry from a recent-pause-task row.
     *
     * @param {{ id, name, duration_in_minutes }} pauseTask
     */
    function placeConfirmTaskStart(pauseTask) {
        confirm_task_start.value = {
            id: pauseTask.id ?? null,
            name: pauseTask?.name ?? null,
            duration: pauseTask?.duration_in_minutes ?? 0,
            status: TTL.Task_Pause,
        };
    }

    function restoreLastPauseTask() {
        is_auto_paused.value = false;
        const list = space_paused_items.value;

        const lastPauseTask = list.find((t) => t.id !== task.id);
        const lastWithDuration = list.find(
            (t) => t.duration_in_minutes !== null && t.id !== task.id,
        );

        if (
            list.length === 0 ||
            task.status !== TTL.Task_Stop ||
            is_task_explicitly_stopped.value ||
            !lastPauseTask ||
            !lastWithDuration ||
            task.id !== lastPauseTask?.id
        ) {
            return;
        }

        const recent = is_task_stopped.value ? lastPauseTask : list[0];
        task.id = recent.id;
        task.name = recent.name;
        task.duration = recent.duration_in_minutes;
        task.status = TTL.Task_Pause;
        task.hasStarted = false;
        restoreTaskTimer();
        is_auto_paused.value = true;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // API CALLS — LOAD
    // Fetch data from the server and hydrate local state via place* functions.
    // Use `noLoading: true` when calling from background contexts (e.g. timer
    // sync reconnect) to avoid a spinner the user did not trigger.
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Full shift + task + workday snapshot. Call on login, reconnect, and
     * whenever a LiveSync event indicates the server state may have diverged.
     *
     * @param {{ noLoading?: boolean }} [options]
     */
    function syncShiftStatusFromServer(options = {}) {
        return manageAsyncOperation(
            async () => {
                const { data } = await axios.post(URLS.shiftStatus);
                placeShiftSnapshotFromServer(data);
            },
            notifyTimeTrackerApiError,
            (v) => {
                isLoadingShiftSnapshot.value = v;
            },
            options.noLoading ?? false,
        );
    }

    /**
     * Refreshes the paused-task list and re-evaluates which paused task should
     * become the active task (if the current task was stopped).
     */
    function syncPausedTaskItemsFromServer() {
        return manageAsyncOperation(
            async () => {
                const { data } = await axios.post(URLS.pausedItems);
                space_paused_items.value = data;
                is_space_paused_items.value = true;
                restoreLastPauseTask();
            },
            notifyTimeTrackerApiError,
            (v) => {
                isLoadingPausedTaskList.value = v;
            },
        );
    }

    /**
     * 3-minute heartbeat sync: posts local counters, corrects drift if the
     * server diverges, and pulls the latest shift source.
     * Always called with `noLoading: true` from the shift-timer interval.
     *
     * @param {{ noLoading?: boolean }} [options]
     */
    function performTimerSyncWithServer(options = {}) {
        return manageAsyncOperation(
            async () => {
                const { data } = await axios.post(URLS.timerSync, {
                    task_status: task.status,
                    shift_status: shift.status,
                    shift_increment:
                        is_shift_in.value || is_shift_resumed.value,
                    task_increment:
                        is_task_started.value || is_task_resumed.value,
                    ...itemIdPayloadFields(task.id),
                });

                const result = data.data;
                const serverTaskStatus = result?.task_status || TTL.Task_Stop;

                if (
                    result?.shift_status !== shift.status ||
                    serverTaskStatus !== task.status
                ) {
                    await syncShiftStatusFromServer({ noLoading: true });
                    return;
                }

                if (result?.shift_in_duration !== undefined) {
                    shift.duration = result.shift_in_duration;
                    if (_shiftTimerInterval.value) {
                        _shiftStartTime.value = Date.now();
                        _shiftBaseSeconds.value = Math.round(shift.duration * 60);
                    } else {
                        restoreShiftTimer();
                    }
                }

                if (result?.task_duration !== undefined) {
                    task.duration = result.task_duration;
                    if (_taskTimerInterval.value) {
                        _taskStartTime.value = Date.now();
                        _taskBaseSeconds.value = Math.round(task.duration * 60);
                    } else {
                        restoreTaskTimer();
                    }
                }

                shift.source = result?.shift_source;
                updateCurrentConversationTaskTime();
            },
            () => {},
            (v) => {
                isSyncingTimer.value = v;
            },
            options.noLoading ?? false,
        );
    }

    /**
     * Partial shift status fetch used when a shift-activity LiveSync event
     * arrives — only updates shift/task status, does not re-init timers.
     */
    function syncShiftStatusAndUpdateTaskOnly() {
        return manageAsyncOperation(
            async () => {
                const { data } = await axios.post(URLS.shiftStatus);
                placePartialShiftFromServer(data);
            },
            notifyTimeTrackerApiError,
            (v) => {
                isLoadingShiftSnapshot.value = v;
            },
        );
    }

    /**
     * @returns {Promise<object[]>} Work log rows for today.
     */
    function getWorkLogs() {
        return manageAsyncOperation(
            async () => {
                const { data } = await axios.get(URLS.shiftWorkLog, {
                    params: {
                        timezone: DateUtilities.getTimezone(),
                        date: new Date().toISOString().substring(0, 10),
                    },
                });
                return data.data;
            },
            notifyTimeTrackerApiError,
            (v) => {
                isLoadingWorkLogs.value = v;
            },
        );
    }

    /**
     * @returns {Promise<boolean>} True when other users in the org are currently on shift.
     */
    async function getWorkdayActiveUsersInfo() {
        try {
            const { data } = await axios.get(
                "/shift-timer/shift/active-user/count",
            );
            let activeUserCount = data?.data ?? 0;
            if (is_to_show_resume_and_stop.value) {
                activeUserCount--;
            }
            return activeUserCount > 0;
        } catch {
            return false;
        }
    }

    /**
     * Loads the Workstation Connect platform download links from the API.
     */
    async function fetchWsDowloadLinks() {
        await manageAsyncOperation(
            async () => {
                const res = await axios.get("app/download-links");
                const data = (res.data ?? [])
                    .slice()
                    .sort((a, b) => b.popularity - a.popularity);
                downloadLinks.value = data;
            },
            () => {},
            (v) => {
                isLoadingDownloadLinks.value = v;
            },
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // SETTERS  (explicit mutation surface for components)
    // Components must call these instead of writing to readonly state directly.
    // ─────────────────────────────────────────────────────────────────────────

    /** Record which task item the user clicked before a start/resume action. */
    function setClickTaskItem(item) {
        click_task_item.value = item;
    }

    /** Flag that the next shift resume was triggered by a task start, not a manual resume. */
    function setShiftResumeByTaskStart(value) {
        shift_resume_by_task_start.value = Boolean(value);
    }

    /** Dismiss the confirm-task-start dialog after the user has acted on it. */
    function clearConfirmTaskStart() {
        confirm_task_start.value = null;
    }

    /**
     * Overwrite the active task identity without calling the API.
     * Used by the task-switch flow before calling taskStart / taskResume.
     *
     * @param {{ id, name, status?, duration? }} fields
     */
    function hydrateTaskSelection({ id, name, status, duration }) {
        task.id = id;
        task.name = name;
        if (status !== undefined) {
            task.status = status;
        }
        if (duration !== undefined) {
            task.duration = duration;
        }
    }

    /**
     * Apply a confirm-task-start entry (from the resume dialog) to the active
     * task, then call `taskResume()` or `taskStart()` from the caller.
     *
     * @param {{ id, name, duration }} confirm
     */
    function applyResumeTaskFromConfirm(confirm) {
        hydrateTaskSelection({
            id: confirm.id,
            name: confirm.name,
            duration: confirm.duration,
        });
    }

    function showWorkstationRequireDialog() {
        ws_confirm_dialog.show = true;
    }
    function hideWorkstationRequireDialog() {
        ws_confirm_dialog.show = false;
    }
    function setWorkstationDialogOpen(show) {
        ws_confirm_dialog.show = Boolean(show);
    }

    /**
     * Patch specific fields of the Workstation Connect dialog.
     *
     * @param {{ title?: string, subtitle?: string, icon?: string }} partial
     */
    function patchWorkstationDialog(partial) {
        if (partial.title != null) {
            ws_confirm_dialog.title = partial.title;
        }
        if (partial.subtitle != null) {
            ws_confirm_dialog.subtitle = partial.subtitle;
        }
        if (partial.icon != null) {
            ws_confirm_dialog.icon = partial.icon;
        }
    }

    /** Called by the WSConnect LiveSync handler — keeps wscAppStatus in sync. */
    function syncWscAppFromLiveSync(wscApp) {
        wscAppStatus.value = wscApp ?? null;
    }

    function updateOfficeHourStatus(status = null) {
        workday_office_status.value = status;
    }

    function blockOneTimeShiftSync() {
        isOneTimeBlockShiftSync.value = true;
    }
    function unBlockOneTimeShiftSync() {
        isOneTimeBlockShiftSync.value = false;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // API CALLS — SHIFT ACTIONS
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Refetch `/shift-timer/shift` so `usr_profile_logged_time` and the right
     * rail match the server immediately after the current user's shift/task
     * mutations (does not rely on LiveSync round-trip).
     */
    async function refreshTeamWorkLogFromServer() {
        await useAppState().fetchLoggedTime();
    }

    /**
     * Workspace item id fields for JSON bodies. Some routes/logging expect both
     * `item_id` and `id` for the same item.
     *
     * @param {number|string|null|undefined} itemId
     * @returns {{ item_id?: number|string, id?: number|string }}
     */
    function itemIdPayloadFields(itemId) {
        if (itemId == null || itemId === "") {
            return {};
        }

        return { item_id: itemId, id: itemId };
    }

    /** @param {object|null} [item] Task to start alongside the shift-in. */
    async function shiftIn(item = null) {
        if (_requiresWorkstationApp()) {
            return;
        }

        shift.status = TTL.Shift_In;

        await manageAsyncOperation(
            async () => {
                useUserStore().updateCurrentUserStatus({
                    shift_resume_at: null,
                    shift_status: TTL.Shift_In,
                });

                const { data } = await axios.post(URLS.shiftLog, {
                    event: TTL.Shift_In,
                    ...itemIdPayloadFields(item?.id),
                });

                shift.status = data?.action;
                const newId = Date.now().toString();
                shift_segment_id.value = newId;
                localStorage.setItem("shift_segment_id", newId);
                startShiftTimer();

                await refreshTeamWorkLogFromServer();

                if (is_task_started.value) {
                    startTaskTimer();
                }
                restoreLastPauseTask();

                if (
                    data?.task_resume_dialog &&
                    !shift_resume_by_task_start.value
                ) {
                    const recent = activeTask.value?.id
                        ? activeTask.value
                        : space_paused_items.value[0];
                    if (recent) {
                        placeConfirmTaskStart(recent);
                    }
                }
            },
            (err) => {
                syncShiftStatusFromServer({ noLoading: true });
                Utilities().handleError(err);
            },
            (v) => {
                isSavingShiftAction.value = v;
            },
        );
    }

    async function shiftPause() {
        if (!is_shift_in.value && !is_shift_resumed.value) {
            return;
        }

        stopShiftTimer();
        shift.status = TTL.Shift_Pause;

        if (is_task_started.value || is_task_resumed.value) {
            task.status = TTL.Task_Pause;
            useUserStore().updateCurrentUserStatus({
                task_status: TTL.Task_Pause,
            });
            stopTaskTimer();
        }

        await manageAsyncOperation(
            async () => {
                const { data } = await axios.post(URLS.shiftLog, {
                    event: TTL.Shift_Pause,
                });
                shift.status = data?.action;
                await refreshTeamWorkLogFromServer();
            },
            (err) => Utilities().handleError(err),
            (v) => {
                isSavingShiftAction.value = v;
            },
        );
    }

    async function shiftResume(item = null) {
        if (_requiresWorkstationApp()) {
            return;
        }

        startShiftTimer();
        shift.status = TTL.Shift_Resume;

        await manageAsyncOperation(
            async () => {
                useUserStore().updateCurrentUserStatus({
                    shift_resume_at: null,
                    shift_status: TTL.Shift_Resume,
                });

                const { data } = await axios.post(URLS.shiftLog, {
                    event: TTL.Shift_Resume,
                });
                shift.status = data?.action;

                if (
                    (is_shift_in.value || is_shift_resumed.value) &&
                    !shift_segment_id.value
                ) {
                    const newId = Date.now().toString();
                    shift_segment_id.value = newId;
                    localStorage.setItem("shift_segment_id", newId);
                }

                if (is_task_started.value || is_task_resumed.value) {
                    startTaskTimer();
                } else {
                    stopShiftTimer();
                }

                if (
                    data?.task_resume_dialog &&
                    !shift_resume_by_task_start.value
                ) {
                    const recent = activeTask.value?.id
                        ? activeTask.value
                        : space_paused_items.value[0];
                    if (recent) {
                        placeConfirmTaskStart(recent);
                    }
                }

                await refreshTeamWorkLogFromServer();
            },
            (err) => Utilities().handleError(err),
            (v) => {
                isSavingShiftAction.value = v;
            },
        );

        shift_resume_by_task_start.value = false;
    }

    async function shiftOut() {
        resetShiftTimer();
        shift.status = TTL.Shift_Out;

        await manageAsyncOperation(
            async () => {
                useUserStore().updateCurrentUserStatus({
                    shift_resume_at: null,
                    shift_status: TTL.Shift_Out,
                    task_status: TTL.Task_Stop,
                });
                await axios.post(URLS.shiftLog, { event: TTL.Shift_Out });
                shift_segment_id.value = null;
                localStorage.removeItem("shift_segment_id");
                await refreshTeamWorkLogFromServer();
            },
            (err) => Utilities().handleError(err),
            (v) => {
                isSavingShiftAction.value = v;
            },
        );
    }

    /**
     * Convenience: resumes if paused, starts if out.
     *
     * @param {object|null} [item]
     */
    function startOrResumeShift(item = null) {
        if (is_shift_in.value || is_shift_resumed.value) {
            return;
        }
        is_shift_paused.value ? shiftResume(item) : shiftIn(item);
    }

    async function setFutureShiftResume(time_string) {
        await manageAsyncOperation(
            async () => {
                await axios.put("/user-profiles/shift-resume", {
                    time: new Date(time_string).toISOString(),
                });
            },
            (e) => console.log("error setting future resume", e),
            null,
            true,
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // API CALLS — TASK ACTIONS
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Start or resume the active task.
     * If the shift is not running it is started first (shift-in gate).
     *
     * @param {(() => void)|null} [successCallback]
     */
    async function taskStart(successCallback = null) {
        is_task_explicitly_stopped.value = false;

        if (_requiresWorkstationApp(true)) {
            return;
        }
        if (!task.status || is_task_stopped.value || is_task_paused.value) {
            if (is_shift_paused.value || is_shift_out.value) {
                await shiftIn(task);
            }

            useUserStore().updateCurrentUserStatus({
                task_status: TTL.Task_Start,
            });
            startTaskTimer();

            const flagForApi = is_task_paused.value
                ? TTL.Task_Resume
                : TTL.Task_Start;
            task.status = flagForApi;

            await manageAsyncOperation(
                async () => {
                    const { data } = await axios.post(
                        `${URLS.taskLog}/${task.id}`,
                        {
                            flag: flagForApi,
                            ...itemIdPayloadFields(task.id),
                        },
                    );

                    task.status = data?.message?.action;

                    if (
                        task.status !== TTL.Task_Resume &&
                        task.status !== TTL.Task_Start
                    ) {
                        task.status = TTL.Task_Stop;
                        Utilities().showErrorSnackbar(
                            "Task is not started. Please refresh and try again",
                        );
                        return;
                    }

                    task.duration = data?.message?.assignment?.duration_in_minutes ?? 0;
                    task.hasStarted = true;
                    restoreTaskStatus();

                    useUserStore().userProfileSetActiveTask(task);

                    await syncActiveTaskDurationBaselines(task.id);
                    await openConversationPanelForActiveTask();
                    await refreshTeamWorkLogFromServer();
                    successCallback?.();
                },
                (err) => Utilities().handleError(err),
                (v) => {
                    isSavingTaskAction.value = v;
                },
            );
        }
    }

    /**
     * @param {boolean} [dummyStatus=false]  When true, only updates local state
     *                                        without posting to the API (used in
     *                                        shift-pause flows).
     */
    async function taskPause(dummyStatus = false) {
        if (!is_task_started.value && !is_task_resumed.value) {
            return;
        }

        is_auto_paused.value = false;
        stopTaskTimer();
        task.status = TTL.Task_Pause;
        task.hasStarted = false;

        if (dummyStatus) {
            return;
        }

        await manageAsyncOperation(
            async () => {
                const userStore = useUserStore();
                userStore.updateCurrentUserStatus({
                    task_status: TTL.Task_Stop,
                });

                const { data } = await axios.post(
                    `${URLS.taskLog}/${task.id}`,
                    {
                        flag: TTL.Task_Pause,
                        ...itemIdPayloadFields(task.id),
                    },
                );
                const taskDuration = taskTimerToMinutes(task.time);

                if (!space_paused_items.value.find((t) => t.id === task.id)) {
                    const pauseItem = {
                        id: task.id,
                        name: task.name,
                        duration_in_minutes: taskDuration,
                        total_duration: taskDuration,
                        task_status: TTL.Task_Pause,
                        is_backlog: false,
                        item_category: data?.message?.item_category,
                    };
                    // unshift so it appears at the top of the paused list.
                    space_paused_items.value.unshift(pauseItem);
                }

                userStore.userProfileSetActiveTask(task);
                await syncPausedTaskItemsFromServer();
                await refreshTeamWorkLogFromServer();
            },
            (err) => Utilities().handleError(err),
            (v) => {
                isSavingTaskAction.value = v;
            },
        );
    }

    async function taskResume(resumeTask = null) {
        is_task_explicitly_stopped.value = false;
        useUserStore().updateCurrentUserStatus({
            task_status: TTL.Task_Resume,
        });

        if (_requiresWorkstationApp(true)) {
            return;
        }

        if (resumeTask) {
            task.id = resumeTask.id;
            task.name = resumeTask.name;
        }

        // Already running — nothing to do.
        if (
            is_task_stopped.value ||
            is_task_started.value ||
            is_task_resumed.value
        ) {
            return false;
        }
        if (!task.id) {
            return false;
        }

        seedActiveTaskDurationFromPausedList(task.id);

        startTaskTimer();
        task.status = TTL.Task_Resume;

        await manageAsyncOperation(
            async () => {
                const { data } = await axios.post(
                    `${URLS.taskLog}/${task.id}`,
                    {
                        flag: TTL.Task_Resume,
                        ...itemIdPayloadFields(task.id),
                    },
                );

                task.status = data?.message?.action;

                if (
                    task.status !== TTL.Task_Resume &&
                    task.status !== TTL.Task_Start
                ) {
                    task.status = TTL.Task_Stop;
                    Utilities().showErrorSnackbar(
                        "Task is not resumed. Please refresh and try again",
                    );
                    return;
                }

                task.duration =
                    data?.message?.assignment?.duration_in_minutes ?? 0;
                task.hasStarted = true;
                restoreTaskStatus();

                useUserStore().userProfileSetActiveTask(task);
                await syncActiveTaskDurationBaselines(task.id);
                await openConversationPanelForActiveTask();
                await syncPausedTaskItemsFromServer();
                await refreshTeamWorkLogFromServer();
            },
            (err) => Utilities().handleError(err),
            (v) => {
                isSavingTaskAction.value = v;
            },
        );
    }

    /**
     * @param {number|null} [taskId=null]  Stop a specific task by ID without
     *                                      stopping a different active task.
     */
    async function taskStop(taskId = null) {
        const actionTaskId = taskId && taskId !== task.id ? taskId : task.id;
        const isCurrentTask = !taskId || taskId === task.id;

        if (isCurrentTask) {
            if (!is_task_started.value && !is_task_resumed.value && !taskId) {
                return;
            }
            stopTaskTimer();
            task.status = TTL.Task_Stop;
            task.hasStarted = false;
        }

        await manageAsyncOperation(
            async () => {
                const userStore = useUserStore();
                userStore.updateCurrentUserStatus({
                    task_status: TTL.Task_Stop,
                });
                const { data } = await axios.post(`${URLS.taskLog}/${actionTaskId}`, {
                    flag: TTL.Task_Stop,
                    activity: isCurrentTask,
                    ...itemIdPayloadFields(actionTaskId),
                });

                if (isCurrentTask) {
                    is_task_explicitly_stopped.value = true;
                }
                const taskDuration = taskTimerToMinutes(task.time);

                if (!space_paused_items.value.find((t) => t.id === task.id)) {
                    const pauseItem = {
                        id: task.id,
                        name: task.name,
                        duration_in_minutes: taskDuration,
                        total_duration: taskDuration,
                        is_backlog: false,
                        item_category: data?.message?.item_category,
                    };
                    // unshift so it appears at the top of the paused list.
                    space_paused_items.value.unshift(pauseItem);
                }

                userStore.userProfileSetActiveTask(task);
                await refreshTeamWorkLogFromServer();
            },
            (err) => Utilities().handleError(err),
            (v) => {
                isSavingTaskAction.value = v;
            },
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // API CALLS — WORKDAY ACTIONS
    // ─────────────────────────────────────────────────────────────────────────

    async function startWorkday() {
        await manageAsyncOperation(
            async () => {
                await axios.post(URLS.workdayStart);
                workday_status.value = TTL.Workday_Started;
                await syncShiftStatusFromServer({ noLoading: true });
            },
            (error) => {
                if (error?.response?.status === HttpStatusCodes.FORBIDDEN) {
                    workday_status.value = TTL.Workday_Stopped;
                }
                Utilities().handleError(error);
            },
            (v) => {
                isSavingWorkday.value = v;
            },
        );
    }

    /** Placeholder — workday-pause is not yet implemented. */
    function pauseWorkday() {
        return;
    }

    async function endWorkday() {
        await manageAsyncOperation(
            async () => {
                await axios.post(URLS.workdayEnd);
                workday_status.value = TTL.Workday_Stopped;
            },
            (err) => Utilities().handleError(err),
            (v) => {
                isSavingWorkday.value = v;
            },
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // LIVE SYNC RECEIVERS
    // Called exclusively from Services/LiveSync handlers — never from components
    // or user-action code.  These are the only functions that should respond to
    // server-push events.
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Called when a task is un-assigned from the current user via LiveSync.
     * Clears the task from the timer if it matches the active one.
     */
    async function restoreTimerOnEndAssignment(
        profile,
        actionPerformedBy,
        _action,
    ) {
        if (!useUserStore().isCurrentProfile(profile?.id)) {
            return;
        }

        const oldTaskId = task.id;
        const oldTaskName = task.name;

        await syncShiftStatusFromServer();
        await syncPausedTaskItemsFromServer();

        if (oldTaskId !== task.id && profile?.id !== actionPerformedBy) {
            const actor = useUserStore().getUserProfile(actionPerformedBy);
            const actorName = actor?.display_name || actor?.full_name;
            Utilities().showInfoSnackbar(
                `<b>${actorName}</b> has ended your task ${oldTaskName}. Please select a new task.`,
            );
            window.notifyWith(actorName, "Task Stopped");
        }
    }

    /**
     * Called when a ShiftActionPerformed event arrives for any profile.
     * Only updates the local timer if the event is for the current user.
     */
    async function restoreTimerOnShiftActivity(
        userProfileId,
        action,
        shiftStatusValue,
    ) {
        const userStore = useUserStore();
        const profile = userStore.getUserProfile(userProfileId);
        if (profile && shiftStatusValue) {
            profile.shift_status = shiftStatusValue;
        }
        if (!userStore.isCurrentProfile(userProfileId)) {
            return;
        }
        if (action !== shift.status) {
            await syncShiftStatusAndUpdateTaskOnly();
        }
    }

    /**
     * Called when the organisation workday status changes via LiveSync.
     */
    async function restoreTimerOnWorkDayEnd(status) {
        const taskWasRunning = is_task_started.value || is_task_resumed.value;
        workday_status.value = status;
        stopShiftTimer();

        await syncShiftStatusFromServer();

        if (taskWasRunning && status === TTL.Workday_Stopped) {
            Utilities().showInfoSnackbar("Workday has ended.");
            window.notifyWith("Workday", "Workday has ended.");
        }
    }

    /**
     * Called by the WorkdayStatusChanged LiveSync event.
     */
    function onWorkdayStatusChanged(_organizationId, status) {
        restoreTimerOnWorkDayEnd(status);
    }

    /**
     * Called when an item name changes via LiveSync — keeps the paused list and
     * active task name in sync.
     */
    function onItemUpdate(item) {
        const paused = space_paused_items.value.find((ti) => ti.id === item.id);
        if (paused) {
            paused.item_name = item.name;
        }
        if (task.id === item.id) {
            task.name = item.name;
        }
    }

    /**
     * Called by the app-activity monitor when the browser tab becomes visible.
     */
    function onAppActive() {
        if (is_shift_in.value || is_shift_resumed.value) {
            performTimerSyncWithServer({ noLoading: true });
        }
    }

    // ─────────────────────────────────────────────────────────────────────────
    // PAUSED ITEMS — local mutations
    // These mutate the paused-task list without an API round-trip.
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Remove a task from the paused list when it is unassigned from the user.
     * Resets the active task if it was the one being tracked.
     */
    function removePauseTaskOnTaskUnAssigned(itemId, profileId) {
        if (!useUserStore().isCurrentProfile(profileId)) {
            return;
        }
        space_paused_items.value = space_paused_items.value.filter(
            (i) => i.id !== itemId,
        );
        if (task.id === itemId) {
            stopTaskTimer();
            resetTaskState();
        }
    }

    /** @param {{ id, name }} newItem */
    function updatePausedItemName(newItem) {
        if (newItem?.id == null) {
            return;
        }

        space_paused_items.value = space_paused_items.value.map((entry) => {
            if (entry.id != newItem.id) {
                return entry;
            }

            return { ...toRaw(entry), name: newItem.name };
        });
    }

    /** @param {{ id, name }} newItemCategory */
    function updatePausedItemGroupName(newItemCategory) {
        space_paused_items.value = space_paused_items.value.map((item) =>
            item.item_category.id == newItemCategory.id
                ? {
                      ...item,
                      item_category: {
                          ...item.item_category,
                          name: newItemCategory.name,
                      },
                  }
                : item,
        );
    }

    /**
     * @param {number[]} itemIds
     * @param {object}   itemCategory
     */
    function updatePauseItemCategory(itemIds, itemCategory) {
        space_paused_items.value = space_paused_items.value.map((item) =>
            itemIds.includes(item.id)
                ? { ...item, item_category: itemCategory }
                : item,
        );
    }

    function resetTask() {
        resetTaskState();
    }

    // ─────────────────────────────────────────────────────────────────────────
    // TASK DURATION  (viewed-task time tracking panel)
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Fetches today's tracked minutes for `itemId` and writes them into every
     * downstream store that surfaces a duration display.
     *
     * @param {number|null} itemId
     * @returns {Promise<number|null>} Today's duration in minutes, or null.
     */
    async function getViewedTaskTimerDuration(itemId) {
        if (!itemId) {
            return null;
        }

        return manageAsyncOperation(
            async () => {
                const { todayMinutes, totalMinutes } =
                    await fetchTaskDurationBaselinesFromServer(itemId);

                applyTaskDurationBaselinesToStores(
                    itemId,
                    todayMinutes,
                    totalMinutes,
                );

                viewed_task.id = itemId;
                viewed_task.duration = todayMinutes;
                viewed_task.total_time = totalMinutes;

                return todayMinutes;
            },
            () => console.log("Task duration not found"),
            (v) => {
                isLoadingViewedTaskDuration.value = v;
            },
        );
    }

    function updateCurrentConversationTaskTime() {
        getViewedTaskTimerDuration(
            useConversation().active?.originalData?.item?.id,
        );
    }

    function setViewedTask(val) {
        viewed_task.id = val.id;
        viewed_task.duration = val.duration;
        viewed_task.total_time = val.total_time;

        if (val.id === task.id && val.total_time != null) {
            setActiveTaskDurationBaselines(
                val.id,
                val.duration ?? 0,
                val.total_time,
            );
        }
    }

    // ─────────────────────────────────────────────────────────────────────────
    // CONVERSATION
    // ─────────────────────────────────────────────────────────────────────────

    async function openConversationPanelForActiveTask() {
        if (!task.id) {
            return;
        }
        const active = useConversation().active;
        const activeItemId = active?.originalData?.item?.id;
        if (
            activeItemId != null &&
            String(activeItemId) === String(task.id)
        ) {
            Utilities().toggleChatPopover(true);
            return;
        }
        const item = useWorkspaceTree().getItemById(task.id) ?? {
            id: task.id,
            name: task.name,
        };
        await useConversation().openConversationForItem(item);
        Utilities().toggleChatPopover(true);
    }

    // ─────────────────────────────────────────────────────────────────────────
    // HELPERS  (pure status predicates — no side effects)
    // Accept an arbitrary status string so they can be used for both the
    // current user's tracker and for member-rail rendering.
    // ─────────────────────────────────────────────────────────────────────────

    function isTaskPause(status) {
        return status === TTL.Task_Pause;
    }
    function isTaskStop(status) {
        return status === TTL.Task_Pause || status === TTL.Task_Stop;
    }
    function isTaskRunning(status) {
        return status === TTL.Task_Start || status === TTL.Task_Resume;
    }

    function isShiftPause(status) {
        return status === TTL.Shift_Pause;
    }
    function isShiftOut(status) {
        return status === TTL.Shift_Out;
    }
    function isShiftNotShiftedIn(status) {
        return status === TTL.Not_Shifted_In;
    }
    function isShiftRunning(status) {
        return status === TTL.Shift_In || status === TTL.Shift_Resume;
    }

    function isShiftPauseUnreachable(status) {
        return status === TTL.Shift_Pause_Unreachable;
    }
    function isShiftPauseInActive(status) {
        return status === TTL.Shift_Pause_InActive;
    }
    function isShiftPauseOrUnreachableOrInActive(status) {
        return (
            isShiftPauseUnreachable(status) ||
            isShiftPause(status) ||
            isShiftPauseInActive(status)
        );
    }

    function isTaskStartedOnItem(taskId) {
        return (
            task.id === taskId &&
            (is_task_started.value || is_task_resumed.value)
        );
    }

    function isTaskPausedOnItem(taskId) {
        const item = space_paused_items.value.find((i) => i.id == taskId);

        if (!item) {
            return false;
        }

        if (item.task_status != null) {
            return item.task_status === TTL.Task_Pause;
        }

        return (
            (item.duration_in_minutes ?? 0) > 0 ||
            (item.total_duration ?? 0) > 0
        );
    }

    function isTaskOrShiftActive() {
        return (
            is_task_started.value ||
            is_task_resumed.value ||
            is_shift_in.value ||
            is_shift_resumed.value
        );
    }

    function isForceUseAppForShiftStart() {
        return useUserStore().getCurrentUserProfile().is_force_app_shift;
    }

    /**
     * @param {string} text   Template string containing `{title}` and `{current_task_title}` tokens.
     * @param {string} name   The incoming task name that replaces `{title}`.
     * @returns {string}
     */
    function setTaskNameInTitle(text, name) {
        return text
            .replace("{title}", `<b>${name}</b>`)
            .replace("{current_task_title}", `<b>${task.name}</b>`);
    }

    function taskTimerToMinutes(taskTime) {
        return taskTime.minutes + taskTime.hours * 60;
    }

    function pausedTaskDuration(itemId) {
        const row = space_paused_items.value.find((i) => i.id == itemId);
        return row?.total_duration
            ? convertMinutesToHoursAndMinutes(row.total_duration)
            : "00 : 00";
    }

    function convertMinutesToHoursAndMinutes(durationMinutes) {
        const h = Math.floor(durationMinutes / 60);
        const m = Math.round((durationMinutes / 60 - h) * 60);
        return `${String(h).padStart(2, "0")} : ${String(m).padStart(2, "0")}`;
    }

    /**
     * Server-synced all-time/today baselines for track the running task even
     * when the user is viewing a different conversation.
     *
     * @returns {{ baselineTotal: number, baselineToday: number }}
     */
    function resolveActiveTaskDurationBaselines() {
        if (
            active_task_duration.id === task.id &&
            active_task_duration.total_time != null
        ) {
            return {
                baselineTotal: active_task_duration.total_time ?? 0,
                baselineToday: active_task_duration.duration ?? 0,
            };
        }

        if (viewed_task.id === task.id) {
            return {
                baselineTotal: viewed_task.total_time ?? 0,
                baselineToday: viewed_task.duration ?? 0,
            };
        }

        const pausedRow = space_paused_items.value.find((i) => i.id === task.id);
        if (pausedRow?.total_duration != null) {
            return {
                baselineTotal: pausedRow.total_duration ?? 0,
                baselineToday:
                    pausedRow.duration_in_minutes ?? task.duration ?? 0,
            };
        }

        const treeTotal =
            useWorkspaceTree().getItemById(task.id)?.version
                ?.duration_in_minutes ?? 0;
        if (treeTotal > 0) {
            return {
                baselineTotal: treeTotal,
                baselineToday: task.duration ?? 0,
            };
        }

        return {
            baselineTotal: task.duration ?? 0,
            baselineToday: task.duration ?? 0,
        };
    }

    /**
     * Total (all-time) duration in minutes for any task id, matching the
     * paused-task total_duration display. For the currently active/running
     * task, this live-ticks using the running timer rather than freezing at
     * the last server sync.
     *
     * @param {number|string} taskId
     * @returns {number}
     */
    function getTotalDurationForTask(taskId) {
        if (taskId === task.id) {
            const { baselineTotal, baselineToday } =
                resolveActiveTaskDurationBaselines();
            const priorToToday = Math.max(0, baselineTotal - baselineToday);
            return priorToToday + taskTimerDurationMinutes();
        }

        const row = space_paused_items.value.find((i) => i.id === taskId);
        return row?.total_duration ?? 0;
    }

    function getActiveTimerStatus() {
        return [TTL.Shift_In, TTL.Shift_Resume];
    }
    function getShiftEvents() {
        return [TTL.Shift_In, TTL.Shift_Resume, TTL.Shift_Pause, TTL.Shift_Out];
    }

    /**
     * Human-readable timer line for chat (`MESSAGE.TIMER`), e.g. from
     * `Conversation.addNewMessage` / `sendTimerEventMessage`.
     *
     * @param {string|null|undefined} eventType  e.g. Task_Start, Shift_Pause
     * @returns {string|undefined}
     */
    function getEventMessage(eventType) {
        const messageByType = {
            [TTL.Shift_In]: "Shift Started",
            [TTL.Shift_Pause]: "Shift Paused",
            [TTL.Shift_Resume]: "Shift Resumed",
            [TTL.Shift_Out]: "Shift Stopped",
            [TTL.Task_Start]: "Task Started",
            [TTL.Task_Pause]: "Task Paused",
            [TTL.Task_Resume]: "Task Resumed",
            [TTL.Task_Stop]: "Task Stopped",
        };

        return messageByType[eventType];
    }

    /**
     * Short action label shown in `MessageTimer` next to Task/Shift.
     *
     * @param {string|null|undefined} eventType
     * @returns {string|undefined}
     */
    function getEventAction(eventType) {
        const actions = {
            [TTL.Shift_In]: "Started",
            [TTL.Shift_Pause]: "Paused",
            [TTL.Shift_Resume]: "Resumed",
            [TTL.Shift_Out]: "Stopped",
            [TTL.Task_Start]: "Started",
            [TTL.Task_Pause]: "Paused",
            [TTL.Task_Resume]: "Resumed",
            [TTL.Task_Stop]: "Stopped",
        };

        return actions[eventType];
    }

    // ─────────────────────────────────────────────────────────────────────────
    // PRIVATE HELPERS
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Returns true and shows the Workstation Connect dialog when the user must
     * use the desktop app for shift actions.
     *
     * @param {boolean} [onlyForPausedShift=false]  When true, only blocks if
     *   the shift is paused or stopped — task start can still be allowed while
     *   the shift is already running.
     */
    function _requiresWorkstationApp(onlyForPausedShift = false) {
        const forceApp = isForceUseAppForShiftStart();
        if (!forceApp) {
            return false;
        }

        if (onlyForPausedShift) {
            if (!is_shift_paused.value && !is_shift_out.value) {
                return false;
            }
        }

        showWorkstationRequireDialog();
        return true;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // LEGACY ALIASES  (kept during gradual migration — prefer the sync* names)
    // ─────────────────────────────────────────────────────────────────────────

    const getShiftStatus = syncShiftStatusFromServer;
    const getPausedItems = syncPausedTaskItemsFromServer;
    const syncTaskTimer = performTimerSyncWithServer;
    const getShiftStatusAndUpdateTask = syncShiftStatusAndUpdateTaskOnly;
    const setRestoreTaskStatus = placePartialShiftFromServer;

    // ─────────────────────────────────────────────────────────────────────────
    // PUBLIC SURFACE
    // Grouped to match the caller mental model:
    //   constants → loading → state → computed → api — load → setters →
    //   api — shift → api — task → api — workday → live sync receivers →
    //   paused items → task duration → helpers → legacy
    // ─────────────────────────────────────────────────────────────────────────

    return {
        // ── constants ─────────────────────────────────────────────────────────
        ...TTL,

        // ── loading ───────────────────────────────────────────────────────────
        isLoadingShiftSnapshot,
        isLoadingPausedTaskList,
        isLoadingViewedTaskDuration,
        isLoadingWorkLogs,
        isLoadingDownloadLinks,
        isSavingShiftAction,
        isSavingTaskAction,
        isSavingWorkday,
        isSyncingTimer,

        // ── state — shift ─────────────────────────────────────────────────────
        shift: readonly(shift),

        // ── state — task ──────────────────────────────────────────────────────
        task: readonly(task),
        space_paused_items: shallowReadonly(space_paused_items),
        is_space_paused_items: readonly(is_space_paused_items),
        viewed_task: readonly(viewed_task),
        confirm_task_start: readonly(confirm_task_start),
        click_task_item: readonly(click_task_item),
        is_prepared: readonly(is_prepared),
        is_auto_paused: readonly(is_auto_paused),
        is_task_explicitly_stopped: readonly(is_task_explicitly_stopped),
        shift_resume_by_task_start: readonly(shift_resume_by_task_start),
        isOneTimeBlockShiftSync: readonly(isOneTimeBlockShiftSync),

        // ── state — workday ───────────────────────────────────────────────────
        workday_status: readonly(workday_status),
        workday_started_at: readonly(workday_started_at),
        workday_office_status: readonly(workday_office_status),
        can_edit_workday: readonly(can_edit_workday),

        // ── state — ui ────────────────────────────────────────────────────────
        ws_confirm_dialog: readonly(ws_confirm_dialog),
        wscAppStatus: readonly(wscAppStatus),
        downloadLinks: readonly(downloadLinks),

        // ── computed — shift ──────────────────────────────────────────────────
        is_shift_in,
        is_shift_resumed,
        is_shift_paused,
        is_shift_out,
        has_shift_started,
        is_to_show_resume_and_stop,
        is_shift_source_web,
        activeShift,

        // ── computed — task ───────────────────────────────────────────────────
        is_task_started,
        is_task_resumed,
        is_task_paused,
        is_task_stopped,
        is_display_task_tracker,
        isCurrentShiftPauseOrUnreachableOrInActive,
        activeTask,

        // ── computed — workday ────────────────────────────────────────────────
        is_workday_started,
        is_workday_started_at: readonly(workday_started_at),
        is_show_resume_and_stop_workday,
        is_show_workday_stopped,
        is_workday_pause,
        is_workday_office_open,
        is_workday_office_close,
        is_workday_past_office_hours,
        paused_items,

        // ── api — load ────────────────────────────────────────────────────────
        syncShiftStatusFromServer,
        syncPausedTaskItemsFromServer,
        performTimerSyncWithServer,
        syncShiftStatusAndUpdateTaskOnly,
        getWorkLogs,
        getWorkdayActiveUsersInfo,
        fetchWsDowloadLinks,

        // ── setters ───────────────────────────────────────────────────────────
        setClickTaskItem,
        setShiftResumeByTaskStart,
        clearConfirmTaskStart,
        hydrateTaskSelection,
        applyResumeTaskFromConfirm,
        showWorkstationRequireDialog,
        hideWorkstationRequireDialog,
        setWorkstationDialogOpen,
        patchWorkstationDialog,
        syncWscAppFromLiveSync,
        updateOfficeHourStatus,
        blockOneTimeShiftSync,
        unBlockOneTimeShiftSync,
        setViewedTask,

        // ── api — shift actions ───────────────────────────────────────────────
        shiftIn,
        shiftPause,
        shiftResume,
        shiftOut,
        startOrResumeShift,
        setFutureShiftResume,

        // ── api — task actions ────────────────────────────────────────────────
        taskStart,
        taskPause,
        taskResume,
        taskStop,

        // ── api — workday actions ─────────────────────────────────────────────
        startWorkday,
        pauseWorkday,
        endWorkday,

        // ── live sync receivers ───────────────────────────────────────────────
        restoreTimerOnEndAssignment,
        restoreTimerOnShiftActivity,
        restoreTimerOnWorkDayEnd,
        onWorkdayStatusChanged,
        onItemUpdate,
        onAppActive,

        // ── paused items — local mutations ────────────────────────────────────
        removePauseTaskOnTaskUnAssigned,
        updatePausedItemName,
        updatePausedItemGroupName,
        updatePauseItemCategory,
        restoreLastPauseTask,
        resetTask,

        // ── task duration ─────────────────────────────────────────────────────
        getViewedTaskTimerDuration,
        updateCurrentConversationTaskTime,

        // ── conversation ──────────────────────────────────────────────────────
        openConversationPanelForActiveTask,

        // ── helpers ───────────────────────────────────────────────────────────
        isTaskPause,
        isTaskStop,
        isTaskRunning,
        isTaskStartedOnItem,
        isTaskPausedOnItem,
        isTaskOrShiftActive,
        isShiftPause,
        isShiftOut,
        isShiftNotShiftedIn,
        isShiftRunning,
        isShiftPauseUnreachable,
        isShiftPauseInActive,
        isShiftPauseOrUnreachableOrInActive,
        isForceUseAppForShiftStart,
        setTaskNameInTitle,
        taskTimerToMinutes,
        pausedTaskDuration,
        convertMinutesToHoursAndMinutes,
        getTotalDurationForTask,
        getActiveTimerStatus,
        getShiftEvents,
        getEventMessage,
        getEventAction,

        // ── clock (exposed for timer-display components) ──────────────────────
        startShiftTimer,
        stopShiftTimer,
        resetShiftTimer,
        restoreShiftTimer,
        startTaskTimer,
        stopTaskTimer,
        restoreTaskTimer,
        restoreTaskStatus,
        getShiftTimerDuration: shiftTimerDurationMinutes,
        getTaskTimerDuration: taskTimerDurationMinutes,

        // ── legacy aliases ────────────────────────────────────────────────────
        getShiftStatus,
        getPausedItems,
        syncTaskTimer,
        getShiftStatusAndUpdateTask,
        setRestoreTaskStatus,

        // ── legacy url properties (used directly in some older components) ────
        timer_paused_task_items_url: URLS.pausedItems,
        timer_shift_log_url: URLS.shiftLog,
        timer_shift_work_log_url: URLS.shiftWorkLog,
        timer_shift_status_url: URLS.shiftStatus,
        timer_task_log_url: URLS.taskLog,
        timer_log_sync_url: URLS.timerSync,
        workday_start_url: URLS.workdayStart,
        workday_end_url: URLS.workdayEnd,
        shift_segment_id: readonly(shift_segment_id),
    };
});
